/**
 * Hook für Web Push Notifications
 * Verwaltet die Registrierung, Subscription und Einstellungen für Push-Benachrichtigungen
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../utils/safeToast';
import { useAuth } from '../context/AuthContext';

const API = "/api";

/**
 * Konvertiert einen Base64-URL-String in ein Uint8Array
 * (benötigt für VAPID-Schlüssel)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');
  const [activeHours, setActiveHours] = useState({ start: '09:00', end: '17:00' });
  const [currentlyActive, setCurrentlyActive] = useState(true);
  
  // Prüfen ob Push Notifications unterstützt werden
  useEffect(() => {
    const checkSupport = () => {
      const supported = 
        'serviceWorker' in navigator && 
        'PushManager' in window && 
        'Notification' in window;
      
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
      }
    };
    
    checkSupport();
  }, []);
  
  // Service Worker Nachrichten empfangen (für Navigation bei Klick)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
        navigate(event.data.url);
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleMessage);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [navigate]);
  
  // Status vom Backend laden
  const loadStatus = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/push/status`);
      setIsEnabled(response.data.enabled);
      setActiveHours(response.data.active_hours);
      setCurrentlyActive(response.data.currently_active);
    } catch (error) {
      console.warn('Push status check failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (user && isSupported) {
      loadStatus();
    } else {
      setIsLoading(false);
    }
  }, [user, isSupported, loadStatus]);
  
  // Service Worker holen (nicht neu registrieren - das macht ServiceWorkerUpdater)
  const getServiceWorkerRegistration = useCallback(async () => {
    if (!isSupported) return null;
    
    try {
      // Warte auf bestehende Registrierung oder registriere neu
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js', {
          updateViaCache: 'none'
        });
      }
      await navigator.serviceWorker.ready;
      return registration;
    } catch (error) {
      console.error('Service Worker nicht verfügbar:', error);
      return null;
    }
  }, [isSupported]);
  
  // Push-Benachrichtigungen aktivieren
  const enablePushNotifications = useCallback(async () => {
    if (!isSupported || !user) {
      toast.error('Push-Benachrichtigungen werden nicht unterstützt');
      return false;
    }
    
    setIsLoading(true);
    
    try {
      // 1. Berechtigung anfordern
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        toast.error('Berechtigung für Benachrichtigungen wurde verweigert');
        setIsLoading(false);
        return false;
      }
      
      // 2. Service Worker holen
      const registration = await getServiceWorkerRegistration();
      if (!registration) {
        toast.error('Service Worker konnte nicht initialisiert werden');
        setIsLoading(false);
        return false;
      }
      
      // 3. VAPID Public Key vom Backend holen
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;
      
      // 4. Push-Subscription erstellen
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // 5. Subscription ans Backend senden
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON()
      });
      
      setIsEnabled(true);
      toast.success('Push-Benachrichtigungen aktiviert');
      
      return true;
      
    } catch (error) {
      console.error('Push-Aktivierung fehlgeschlagen:', error);
      toast.error('Push-Benachrichtigungen konnten nicht aktiviert werden');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, user, getServiceWorkerRegistration]);
  
  // Push-Benachrichtigungen deaktivieren
  const disablePushNotifications = useCallback(async () => {
    if (!user) return false;
    
    setIsLoading(true);
    
    try {
      // Subscription im Backend löschen
      await axios.delete(`${API}/push/unsubscribe`);
      
      // Service Worker Subscription entfernen
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }
      
      setIsEnabled(false);
      toast.success('Push-Benachrichtigungen deaktiviert');
      
      return true;
      
    } catch (error) {
      console.error('Push-Deaktivierung fehlgeschlagen:', error);
      toast.error('Fehler beim Deaktivieren der Push-Benachrichtigungen');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user]);
  
  // Toggle-Funktion
  const togglePushNotifications = useCallback(async () => {
    if (isEnabled) {
      return await disablePushNotifications();
    } else {
      return await enablePushNotifications();
    }
  }, [isEnabled, enablePushNotifications, disablePushNotifications]);
  
  return {
    isSupported,
    isEnabled,
    isLoading,
    permission,
    activeHours,
    currentlyActive,
    enablePushNotifications,
    disablePushNotifications,
    togglePushNotifications,
    refreshStatus: loadStatus
  };
};

export default usePushNotifications;
